import React, { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import TextInput from "../../UI/TextInput";
import { Button } from "../../UI/Button";
import {
  mailSenderApiService,
  mailSenderErrorMessage,
  type MailPurpose,
  type MailPurposeAssignment,
  type MailSender,
  type MailSenderProvider,
} from "../../../../infrastructure/api/company/mail-sender-api-service";
import {
  AppPasswordGuideDialog,
  GuideButton,
  MailAccountDialog,
  ProviderNotice,
  ProviderPicker,
  type FormMailProvider,
} from "./mailAccountUi";

const PROVIDER_LABELS: Record<MailSenderProvider, string> = {
  NAVER: "네이버 메일",
  DAUM: "다음(한메일)",
  GOOGLE: "Gmail",
  OTHER: "기타",
};

/** 보내기 전용 계정은 SMTP 서버를 알 수 있는 서비스만 고른다 */
const SENDER_PROVIDERS: Exclude<FormMailProvider, "OTHERS">[] = ["NAVER", "KAKAO", "GMAIL"];
const TO_BACKEND_PROVIDER: Record<Exclude<FormMailProvider, "OTHERS">, MailSenderProvider> = {
  NAVER: "NAVER",
  KAKAO: "DAUM",
  GMAIL: "GOOGLE",
};

const formatDateTime = (value: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const EMPTY_FORM = { provider: "NAVER" as Exclude<FormMailProvider, "OTHERS">, emailAddress: "", password: "", fromAddress: "" };

interface Props {
  companyId: number;
  /** 기본 계정(주문 메일함 계정) 주소. 설정 전이면 비어 있다 */
  defaultEmail?: string | null;
  /** 기본 계정 행의 '수정' */
  onEditDefault?: () => void;
  /** 보내기 전용 계정 추가 다이얼로그 (페이지 우측 상단 '추가'가 연다) */
  showAddDialog?: boolean;
  onCloseAddDialog?: () => void;
}

/**
 * 이메일 설정의 메일 계정 목록과 기능별 발신 계정.
 *
 * 목록 첫 행은 기본 계정(주문 메일함)이다. 주문 메일을 받아 오고, 기능별로 따로 고르지 않은 메일을 보낸다.
 * 그 아래는 보내기 전용으로 추가한 계정이다. 기능 묶음마다 어느 계정으로 보낼지 고른다.
 */
export const MailSenderSettings: React.FC<Props> = ({
  companyId, defaultEmail, onEditDefault, showAddDialog = false, onCloseAddDialog,
}) => {
  const [senders, setSenders] = useState<MailSender[]>([]);
  const [assignments, setAssignments] = useState<MailPurposeAssignment[]>([]);
  const [error, setError] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [adding, setAdding] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      const [loadedSenders, loadedAssignments] = await Promise.all([
        mailSenderApiService.listSenders(companyId),
        mailSenderApiService.listAssignments(companyId),
      ]);
      setSenders(loadedSenders);
      setAssignments(loadedAssignments);
    } catch (e) {
      setError(mailSenderErrorMessage(e, "메일 계정 설정을 불러오지 못했습니다."));
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  const closeAddDialog = () => {
    setForm(EMPTY_FORM);
    setDialogError("");
    onCloseAddDialog?.();
  };

  const handleAdd = async () => {
    const email = form.emailAddress.trim();
    if (!email || !form.password) return;
    // 같은 주소가 목록에 두 번 보이지 않게 막는다 (서버도 같은 규칙으로 막는다)
    const duplicate = [defaultEmail, ...senders.map((s) => s.emailAddress)]
      .some((address) => address && address.toLowerCase() === email.toLowerCase());
    if (duplicate) {
      setDialogError("이미 등록된 메일 주소입니다.");
      return;
    }
    setAdding(true);
    setDialogError("");
    try {
      const created = await mailSenderApiService.addSender(companyId, {
        provider: TO_BACKEND_PROVIDER[form.provider],
        emailAddress: email,
        password: form.password,
        fromAddress: form.fromAddress.trim() || undefined,
      });
      setSenders((previous) => [...previous, created]);
      closeAddDialog();
    } catch (e) {
      setDialogError(mailSenderErrorMessage(e, "계정을 추가하지 못했습니다. 메일 주소와 앱 비밀번호를 확인해 주세요."));
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (sender: MailSender) => {
    setError("");
    try {
      await mailSenderApiService.deleteSender(companyId, sender.id);
      setSenders((previous) => previous.filter((s) => s.id !== sender.id));
    } catch (e) {
      setError(mailSenderErrorMessage(e, "계정을 삭제하지 못했습니다."));
    }
  };

  // 바꾸는 즉시 저장한다. 실패하면 이전 선택으로 되돌린다
  const handleAssign = async (purpose: MailPurpose, senderId: number | null) => {
    const previous = assignments;
    setAssignments((current) => current.map((a) => (a.purpose === purpose ? { ...a, senderId } : a)));
    setError("");
    try {
      setAssignments(await mailSenderApiService.assign(companyId, purpose, senderId));
    } catch (e) {
      setAssignments(previous);
      setError(mailSenderErrorMessage(e, "기능별 발신 계정을 저장하지 못했습니다."));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {error && (
        <p role="alert" className="rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-2.5 text-[13px] text-[#4285F4]">
          {error}
        </p>
      )}

      {/* 메일 계정 목록 */}
      <ul aria-label="메일 계정" className="flex flex-col gap-2">
        <li className="flex items-center gap-3 px-4 py-3 rounded-[12px] border border-[#E0E0E0] bg-white">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[14px] text-[#222] truncate">{defaultEmail || "기본 계정이 없습니다"}</span>
              <span className="px-2 py-0.5 rounded-[4px] bg-[#E8EFFF] text-[#4285F4] text-[11px] font-medium">기본 · 주문 메일 수신</span>
            </div>
            {!defaultEmail && (
              <div className="text-[12px] text-[#888] mt-0.5">우측 상단 '추가'로 주문 메일함 계정을 먼저 등록하세요.</div>
            )}
          </div>
          {defaultEmail && onEditDefault && (
            <button type="button" onClick={onEditDefault} className="text-[13px] font-medium text-[#4285F4] hover:text-[#3367D6]">
              수정
            </button>
          )}
        </li>
        {senders.map((sender) => (
          <li key={sender.id} className="flex items-start gap-3 px-4 py-3 rounded-[12px] border border-[#E0E0E0] bg-white">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[14px] text-[#222] truncate">{sender.emailAddress}</span>
                <span className="px-2 py-0.5 rounded-[4px] bg-[#F5F5F5] text-[#888] text-[11px] font-medium">발신 전용</span>
              </div>
              <div className="text-[12px] text-[#888] mt-0.5">
                {PROVIDER_LABELS[sender.provider]}
                {sender.fromAddress && ` · 보낸 사람 ${sender.fromAddress}`}
                {sender.connectionVerifiedAt && ` · ${formatDateTime(sender.connectionVerifiedAt)} 연결 확인`}
              </div>
              {sender.lastFailureAt && (
                <div className="text-[12px] text-[#4285F4] mt-1">
                  {formatDateTime(sender.lastFailureAt)} 발송 실패: {sender.lastFailureMessage}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => void handleDelete(sender)}
              className="p-1 text-[#BBBBBB] hover:text-[#F24A4A]"
              title="계정 삭제"
              aria-label={`${sender.emailAddress} 삭제`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>

      {/* 기능별 발신 계정 */}
      <section className="bg-white border border-[#E0E0E0] rounded-[12px] p-6">
        <h2 className="text-[16px] font-bold text-[#222] mb-1">기능별 발신 계정</h2>
        <p className="text-[13px] text-[#888] mb-4">
          고르지 않은 기능은 기본 계정으로 보냅니다. 고른 계정으로 보내지 못하면 다른 계정으로 대신 보내지 않습니다.
        </p>
        <ul className="flex flex-col divide-y divide-[#EEEEEE]">
          {assignments.map((assignment) => (
            <li key={assignment.purpose} className="flex flex-col md:flex-row md:items-center gap-2 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-medium text-[#222]">{assignment.label}</div>
                <div className="text-[12px] text-[#888]">{assignment.description}</div>
              </div>
              <select
                aria-label={`${assignment.label} 발신 계정`}
                className="w-full md:w-[260px] border border-[#E0E0E0] rounded-[8px] px-3 py-2.5 text-[14px] text-[#222] bg-white focus:border-[#4285F4] focus:outline-none"
                value={assignment.senderId ?? ""}
                onChange={(e) => void handleAssign(assignment.purpose, e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">기본 계정{defaultEmail ? ` (${defaultEmail})` : ""}</option>
                {senders.map((sender) => (
                  <option key={sender.id} value={sender.id}>{sender.emailAddress}</option>
                ))}
              </select>
            </li>
          ))}
        </ul>
      </section>

      {/* 보내기 전용 계정 추가 — 기본 계정과 같은 입력 UI */}
      {showAddDialog && (
        <MailAccountDialog title="발신 계정 추가" onClose={closeAddDialog}>
          <p className="text-[13px] text-[#888] mb-4">
            기능별로 다른 주소에서 메일을 보낼 때 씁니다. 추가할 때 메일 서버 로그인을 확인합니다.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-[14px] font-medium text-[#333] mb-3">
                메일 서비스 <span className="text-[#F24A4A]">*</span>
              </label>
              <ProviderPicker
                value={form.provider}
                options={SENDER_PROVIDERS}
                onChange={(provider) => setForm({ ...form, provider: provider as Exclude<FormMailProvider, "OTHERS"> })}
              />
              <ProviderNotice provider={form.provider} />
            </div>
            <div>
              <label className="block text-[14px] font-medium text-[#333] mb-2" htmlFor="sender-email">
                발신 이메일 주소 <span className="text-[#F24A4A]">*</span>
              </label>
              <TextInput
                id="sender-email"
                type="email"
                placeholder="noreply@example.com"
                value={form.emailAddress}
                onChange={(e) => setForm({ ...form, emailAddress: e.target.value })}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[14px] font-medium text-[#333]" htmlFor="sender-password">
                  앱 비밀번호 <span className="text-[#F24A4A]">*</span>
                </label>
                <GuideButton onClick={() => setShowGuide(true)} />
              </div>
              <TextInput
                id="sender-password"
                type="password"
                autoComplete="new-password"
                placeholder="앱 비밀번호를 입력하세요"
                value={form.password}
                // 앱 비밀번호는 공백 포함으로 복사되는 경우가 많아 공백을 지운다
                onChange={(e) => setForm({ ...form, password: e.target.value.replace(/\s/g, "") })}
              />
              <p className="text-[12px] text-[#888] mt-1">계정 비밀번호가 아닌, 앱 비밀번호(애플리케이션 비밀번호)를 입력하세요.</p>
            </div>
            <div>
              <label className="block text-[14px] font-medium text-[#333] mb-2" htmlFor="sender-from">
                보낸 사람 주소 (선택)
              </label>
              <TextInput
                id="sender-from"
                type="email"
                placeholder="비우면 발신 이메일 주소로 보냅니다"
                value={form.fromAddress}
                onChange={(e) => setForm({ ...form, fromAddress: e.target.value })}
              />
            </div>
          </div>
          {dialogError && (
            <p role="alert" className="mt-4 rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-2.5 text-[13px] text-[#4285F4]">
              {dialogError}
            </p>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={closeAddDialog} disabled={adding}>
              취소
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleAdd()}
              disabled={adding || !form.emailAddress.trim() || !form.password}
            >
              {adding ? "연결 확인 중..." : "연결 확인 후 추가"}
            </Button>
          </div>
          {showGuide && <AppPasswordGuideDialog provider={form.provider} onClose={() => setShowGuide(false)} />}
        </MailAccountDialog>
      )}
    </div>
  );
};
